import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Image,
} from 'react-native';
import { LinkedAccount } from '@/types/residents/home';
import { LINKED_ACCOUNTS_COLORS } from '@/constants/residents/mobile/linkedAccountsColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { getDisplayName, getInitials } from '@/utils/residents/utils';
import { linkedAccountsStrings } from '@/constants/residents/strings';

const PersonIcon = RESIDENTS_SVG_COMPONENTS.USER;
const PayIcon = RESIDENTS_SVG_COMPONENTS.PAY;
const PropertyIcon = RESIDENTS_SVG_COMPONENTS.PROPERTY;
const ResioLogoFaded = RESIDENTS_SVG_COMPONENTS.RESIO_LOGO_FADED;

interface MobileLinkedAccountCardProps {
  account: LinkedAccount;
  onRemove: (account: LinkedAccount) => void;
  onEmailPress?: (email: string) => void;
}

/**
 * Mobile Linked Account Card Component
 * Matches Figma design for resident mobile linked accounts
 */
export const MobileLinkedAccountCard: React.FC<MobileLinkedAccountCardProps> = ({
  account,
  onRemove,
  onEmailPress,
}) => {
  const { user, sender, property } = account;
  const displayName = getDisplayName(user.firstName, user.lastName);
  const hasName = !!displayName;
  const displayText = hasName ? displayName : user.email;
  
  // Determine if email is long and needs truncation (over ~20 chars typically)
  const isEmailTruncated = !hasName && user.email.length > 20;

  const handleRemovePress = () => {
    onRemove(account);
  };

  const handleNameEmailPress = () => {
    if (isEmailTruncated && onEmailPress) {
      onEmailPress(user.email);
    }
  };

  // Get initials for avatar using shared utility
  const initials = getInitials(user.firstName, user.lastName);
  const showInitials = !!initials;

  // Permission text based on sender flag
  const getPermissionText = (): string => {
    if (sender) {
      return linkedAccountsStrings.cell.sender; // "You can receive payments from this account"
    }
    return linkedAccountsStrings.cell.capabilityGuest; // "You can make payments to this account"
  };

  return (
    <View style={styles.container}>
      {/* Top section with user info */}
      <View style={styles.userSection}>
        <View style={styles.contentRow}>
          {/* Avatar */}
          <View style={styles.avatarContainer}>
            {user.photoUrl ? (
              <Image
                source={{ uri: user.photoUrl }}
                style={styles.avatarImage}
                resizeMode="cover"
              />
            ) : showInitials ? (
              <Text style={styles.avatarInitials}>{initials}</Text>
            ) : (
              <PersonIcon width={20} height={20} color={LINKED_ACCOUNTS_COLORS.textPrimary} />
            )}
          </View>

          {/* Right content */}
          <View style={styles.rightContent}>
            {/* Info section */}
            <View style={styles.infoSection}>
              {/* Name/Email */}
              <TouchableOpacity
                onPress={handleNameEmailPress}
                disabled={!isEmailTruncated}
                activeOpacity={isEmailTruncated ? 0.7 : 1}
              >
                <Text
                  style={styles.nameText}
                  numberOfLines={1}
                  ellipsizeMode="middle"
                >
                  {displayText}
                </Text>
              </TouchableOpacity>

              {/* Permissions */}
              <View style={styles.permissionsContainer}>
                {/* Property info if available */}
                {property && (
                  <View style={styles.permissionRow}>
                    <View style={styles.iconWrapper}>
                      <PropertyIcon width={16} height={16} color={LINKED_ACCOUNTS_COLORS.iconPrimary} />
                    </View>
                    <Text style={styles.permissionText} numberOfLines={2}>
                      {linkedAccountsStrings.cell.currentLease} {property.name}
                    </Text>
                  </View>
                )}

                {/* Payment permission */}
                <View style={styles.permissionRow}>
                  <View style={styles.iconWrapper}>
                    <PayIcon width={16} height={16} color={LINKED_ACCOUNTS_COLORS.iconSecondary} />
                  </View>
                  <Text style={styles.permissionText} numberOfLines={2}>
                    {getPermissionText()}
                  </Text>
                </View>
              </View>
            </View>

            {/* Divider */}
            <View style={styles.divider} />
          </View>
        </View>

        {/* Remove Connection Link */}
        <View style={styles.removeContainer}>
          <TouchableOpacity
            onPress={handleRemovePress}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={linkedAccountsStrings.cell.removeTitle}
          >
            <Text style={styles.removeText}>{linkedAccountsStrings.cell.removeConnection}</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Bottom section with property image or Resio logo placeholder */}
      <View style={styles.logoSection}>
        <View style={styles.logoContainer}>
          {property?.photoUrl ? (
            <Image
              source={{ uri: property.photoUrl }}
              style={styles.propertyImage}
              resizeMode="cover"
            />
          ) : property?.logoUrl ? (
            <Image
              source={{ uri: property.logoUrl }}
              style={styles.propertyLogo}
              resizeMode="contain"
            />
          ) : (
            <View style={styles.logoWrapper}>
              <ResioLogoFaded width={120} height={170} />
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: LINKED_ACCOUNTS_COLORS.cardBackground,
    borderRadius: 16,
    overflow: 'hidden',
    marginBottom: 16,
  },
  userSection: {
    padding: 20,
  },
  contentRow: {
    flexDirection: 'row',
    gap: 16,
  },
  avatarContainer: {
    width: 68,
    height: 68,
    borderRadius: 34,
    backgroundColor: LINKED_ACCOUNTS_COLORS.avatarBackground,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 34,
  },
  avatarInitials: {
    fontSize: 24,
    fontWeight: '700',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 36,
  },
  rightContent: {
    flex: 1,
    gap: 20,
  },
  infoSection: {
    gap: 8,
  },
  nameText: {
    fontSize: 18,
    fontWeight: '600',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 27,
  },
  permissionsContainer: {
    gap: 6,
  },
  permissionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  iconWrapper: {
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  permissionText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 21,
  },
  divider: {
    height: 1,
    backgroundColor: LINKED_ACCOUNTS_COLORS.divider,
  },
  removeContainer: {
    paddingLeft: 84, // 68 (avatar) + 16 (gap)
    marginTop: 20,
  },
  removeText: {
    fontSize: 12,
    fontWeight: '500',
    color: LINKED_ACCOUNTS_COLORS.accentPink,
    lineHeight: 18,
  },
  logoSection: {
    paddingHorizontal: 12,
    paddingBottom: 12,
  },
  logoContainer: {
    height: 211,
    backgroundColor: LINKED_ACCOUNTS_COLORS.background,
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },
  propertyImage: {
    width: '100%',
    height: '100%',
  },
  propertyLogo: {
    width: '100%',
    height: '100%',
    backgroundColor: LINKED_ACCOUNTS_COLORS.background,
  },
  logoWrapper: {
    position: 'absolute',
    right: -20,
    bottom: -40,
  },
});

